import {customElement, property} from "lit/decorators.js";
import type {TemplateResult} from "lit";
import {html} from "lit";
import {UnLitElement} from "../../../common/elements";
import {formDeleteUploadEvent} from "../formEvents";
import {Dictionary} from "../../../page/elements/dictionary";
import {LocatedNumbers} from "../../../common/utils/locatedNumbers";
import {resolve} from "../../../container";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";


const KB_MULTIPLE = 1024;
const MB_MULTIPLE = KB_MULTIPLE * KB_MULTIPLE;

enum InputState {
    UPLOADING,
    UPLOADED
}

@customElement("eop-form-uploaded-file")
export class EopFormUploadedFile extends UnLitElement {

    @property({reflect: true})
    public key: string;

    public constructor(public name: string, public size: number, private locatedNumbers: LocatedNumbers = resolve(LocatedNumbers)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.updateProgress(this.key ? 100 : 0);
    }

    public render(): TemplateResult {
        return html`
            <div class="file-row">
                ${this.renderFileInfo()}
                ${this.renderDeleteButton()}
            </div>
            ${this.renderProgressBar()}
        `;
    }

    public updateProgress(progress: number): void {
        const roundedProgress = Math.round(progress);
        this.style.setProperty("--progress", roundedProgress + "%");
    }

    private renderFileInfo(): TemplateResult {
        return html`
            <div class="file-name">${this.name} <span class="file-size">(${this.readableFileSize()})</span></div>`;
    }

    private renderProgressBar(): TemplateResult {
        return html`
            <div class="progress-bar"></div>`;
    }

    private renderDeleteButton(): Nullable<TemplateResult> {
        const deleteMsg = Dictionary.of(this).translate("DELETE_FILE");
        if (this.inputState() !== InputState.UPLOADED) {
            return null;
        }

        return html`
            <button type="button" class="delete" @click=${this.deleteFile} title=${deleteMsg} ${trackingButtonInteract("delete-uploaded-file")}>
                ${this.renderTrashIcon()}
            </button>`;
    }

    private deleteFile(): void {
        this.dispatchEvent(formDeleteUploadEvent(this.key));
    }

    private inputState(): InputState {
        if (this.key) {
            return InputState.UPLOADED;
        }
        return InputState.UPLOADING;
    }

    private renderTrashIcon(): TemplateResult {
        return html`<eop-svg-sprite-icon name="trash"></eop-svg-sprite-icon>`;
    }

    private readableFileSize(): string {
        if (this.size >= MB_MULTIPLE) {
            return this.locatedNumbers.formatNumberString((this.size / MB_MULTIPLE).toFixed(2)) + " MB";
        }
        return this.locatedNumbers.formatNumberString((this.size / KB_MULTIPLE).toFixed(2) + " KB");
    }
}